import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { Text, Trash2 } from "lucide-react";

import { FloatingPlacement } from "@/api/document/floats";
import type { FloatImage, FloatPage } from "@/api/document/floats";
import { cn } from "@/lib/utils";

import { ControlButton, ResizeHandles } from "./resize-handles";
import type { Corner } from "./resize-handles";

const MIN_WIDTH = 16;

const controlGuard = {
	onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => event.stopPropagation(),
};

export interface FloatSelection {
	id: number;
	page: number;
}

export interface FloatBox {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface FloatingLayerProps {
	floats: FloatImage[];
	pages: FloatPage[];
	stride: number;
	pageWidth: number;
	pageHeight: number;
	selection: FloatSelection | null;
	onSelect(selection: FloatSelection | null): void;
	onCommit(id: number, box: FloatBox): void;
	onToFlow(id: number): void;
	onRemove(id: number): void;
}

interface Drag {
	pointerId: number;
	id: number;
	startX: number;
	startY: number;
	base: FloatBox;
	corner: Corner | null;
}

function clampBox(box: FloatBox, pageWidth: number, pageHeight: number): FloatBox {
	const width = Math.min(box.width, pageWidth);
	const height = Math.min(box.height, pageHeight);

	return {
		x: Math.round(Math.max(0, Math.min(box.x, pageWidth - width))),
		y: Math.round(Math.max(0, Math.min(box.y, pageHeight - height))),
		width: Math.round(width),
		height: Math.round(height),
	};
}

function dragBox(drag: Drag, clientX: number, clientY: number): FloatBox {
	const dx = clientX - drag.startX;
	const dy = clientY - drag.startY;

	if (drag.corner === null) {
		return { ...drag.base, x: drag.base.x + dx, y: drag.base.y + dy };
	}

	const aspect = drag.base.height / drag.base.width;
	const growth = drag.corner === "nw" || drag.corner === "sw" ? -dx : dx;
	const width = Math.max(MIN_WIDTH, drag.base.width + growth);
	const height = width * aspect;

	return {
		x:
			drag.corner === "nw" || drag.corner === "sw"
				? drag.base.x + drag.base.width - width
				: drag.base.x,
		y:
			drag.corner === "nw" || drag.corner === "ne"
				? drag.base.y + drag.base.height - height
				: drag.base.y,
		width,
		height,
	};
}

export function FloatingLayer({
	floats,
	pages,
	stride,
	pageWidth,
	pageHeight,
	selection,
	onSelect,
	onCommit,
	onToFlow,
	onRemove,
}: FloatingLayerProps) {
	const dragRef = useRef<Drag | null>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const [preview, setPreview] = useState<{ id: number; box: FloatBox } | null>(null);

	const paints = FloatingPlacement.resolve(pages, floats);

	const applyMove = (pointerId: number, clientX: number, clientY: number) => {
		const drag = dragRef.current;

		if (drag === null || pointerId !== drag.pointerId) {
			return;
		}

		setPreview({
			id: drag.id,
			box: clampBox(dragBox(drag, clientX, clientY), pageWidth, pageHeight),
		});
	};

	const applyEnd = (pointerId: number, clientX: number, clientY: number) => {
		const drag = dragRef.current;

		if (drag === null || pointerId !== drag.pointerId) {
			return;
		}

		const box = clampBox(dragBox(drag, clientX, clientY), pageWidth, pageHeight);
		const moved =
			box.x !== drag.base.x ||
			box.y !== drag.base.y ||
			box.width !== drag.base.width ||
			box.height !== drag.base.height;

		dragRef.current = null;
		setPreview(null);

		if (moved) {
			onCommit(drag.id, box);
		}
	};

	const shared = useRef({ paints, stride, pageWidth, pageHeight, onSelect, applyMove, applyEnd });
	shared.current = { paints, stride, pageWidth, pageHeight, onSelect, applyMove, applyEnd };

	useEffect(() => {
		if (selection === null) {
			return;
		}

		const onPointerDown = (event: PointerEvent) => {
			if (
				!event.defaultPrevented &&
				rootRef.current !== null &&
				!rootRef.current.contains(event.target as Node)
			) {
				onSelect(null);
			}
		};

		document.addEventListener("pointerdown", onPointerDown);

		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [selection, onSelect]);

	useEffect(() => {
		const strip = rootRef.current?.parentElement;

		if (strip === null || strip === undefined) {
			return;
		}

		const onDown = (event: PointerEvent) => {
			if (event.button !== 0) {
				return;
			}

			const target = event.target as HTMLElement;
			const root = rootRef.current;

			if (root === null || root.contains(target)) {
				return;
			}

			const content = target.closest(".tiptap");

			if (content !== null && target !== content && target.closest(".page-gap") === null) {
				return;
			}

			const rect = strip.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const total = event.clientY - rect.top;
			const page = Math.floor(total / shared.current.stride);
			const y = total - page * shared.current.stride;
			const pagePaints = shared.current.paints.at(page);

			if (pagePaints === undefined || page < 0 || y > shared.current.pageHeight) {
				return;
			}

			const float = pagePaints.findLast(
				(paint) =>
					x >= paint.x && x <= paint.x + paint.width && y >= paint.y && y <= paint.y + paint.height,
			);

			if (float === undefined) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			shared.current.onSelect({ id: float.id, page });
			dragRef.current = {
				pointerId: event.pointerId,
				id: float.id,
				startX: event.clientX,
				startY: event.clientY,
				base: { x: float.x, y: float.y, width: float.width, height: float.height },
				corner: null,
			};

			const detach = () => {
				document.removeEventListener("pointermove", onMove);
				document.removeEventListener("pointerup", onUp);
				document.removeEventListener("pointercancel", onCancel);
			};
			const onMove = (move: PointerEvent) =>
				shared.current.applyMove(move.pointerId, move.clientX, move.clientY);
			const onUp = (up: PointerEvent) => {
				shared.current.applyEnd(up.pointerId, up.clientX, up.clientY);
				detach();
			};
			const onCancel = () => {
				dragRef.current = null;
				setPreview(null);
				detach();
			};

			document.addEventListener("pointermove", onMove);
			document.addEventListener("pointerup", onUp);
			document.addEventListener("pointercancel", onCancel);
		};

		strip.addEventListener("pointerdown", onDown, true);

		return () => strip.removeEventListener("pointerdown", onDown, true);
	}, []);

	const start =
		(float: FloatImage, page: number, corner: Corner | null) =>
		(event: ReactPointerEvent<HTMLElement>) => {
			if (event.button !== 0) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			event.currentTarget.setPointerCapture(event.pointerId);
			dragRef.current = {
				pointerId: event.pointerId,
				id: float.id,
				startX: event.clientX,
				startY: event.clientY,
				base: { x: float.x, y: float.y, width: float.width, height: float.height },
				corner,
			};
			onSelect({ id: float.id, page });
		};

	const move = (event: ReactPointerEvent<HTMLElement>) => {
		applyMove(event.pointerId, event.clientX, event.clientY);
	};

	const end = (event: ReactPointerEvent<HTMLElement>) => {
		applyEnd(event.pointerId, event.clientX, event.clientY);
	};

	return (
		<div ref={rootRef} className="doc-float-layer" contentEditable={false}>
			{paints.flatMap((page, pageIndex) =>
				page.map((float) => {
					const selected = selection?.id === float.id;
					const active = selected && selection.page === pageIndex;
					const box =
						preview !== null && preview.id === float.id
							? preview.box
							: { x: float.x, y: float.y, width: float.width, height: float.height };

					return (
						<div
							key={`${float.id}-${pageIndex}`}
							className={cn("doc-float", selected && "doc-float-selected")}
							style={{
								top: pageIndex * stride + box.y,
								left: box.x,
								width: box.width,
								height: box.height,
							}}
							onPointerDown={start(float, pageIndex, null)}
							onPointerMove={move}
							onPointerUp={end}
						>
							<img src={`/terms/image/${float.file}`} alt="" draggable={false} />

							{active && (
								<ResizeHandles
									onCornerPointerDown={(corner) => start(float, pageIndex, corner)}
									onPointerMove={move}
									onPointerUp={end}
								/>
							)}

							{active && (
								<span className="absolute -top-10 left-0 z-20 flex items-center gap-0.5 whitespace-nowrap surface-panel p-1 shadow-card">
									<ControlButton
										label="Voltar pro fluxo do texto"
										guard={controlGuard}
										onClick={() => onToFlow(float.id)}
									>
										<Text />
									</ControlButton>

									<ControlButton
										label="Remover imagem"
										guard={controlGuard}
										onClick={() => onRemove(float.id)}
									>
										<Trash2 />
									</ControlButton>
								</span>
							)}
						</div>
					);
				}),
			)}
		</div>
	);
}
